import MarketPrice from "@/common/components/marketPrice";
import { H3 } from "@/common/elements/headings";
import {
  COLD_STORAGE_CEILING_C,
  COLD_STORAGE_FLOOR_C,
  getLogisticCompaniesDetails,
  getReceivingCompanyOptions,
  packageType,
  temperatureOption,
  vehicleOptions,
} from "../data";
import useQuery from "@/hooks/useQuery";
import { LOGISTICS_COMPANIES_URL } from "@/common/constants";

import { useState } from "react";
import { todayAsDateString } from "@/common/utils/isContain";
import { KeyValueObj } from "@/common/types";
import { Select } from "@/common/components/selectInput";
import Input from "@/common/components/Input/Input";
import { AddressManager } from "@/common/components/addresses";
import Form from "@/common/components/form/Form";
import { Button } from "@/common/components/button";
import { getNewDeliveryAddressFormInputs } from "@/common/components/addresses/data";

import { MultiSelect } from "@/common/components/multi-selectInput";
import { statesOnly } from "@/common/data/statesOnly";
interface IProps {
  formData: KeyValueObj;
  onContinue: () => void;
  onBack: () => void;
  onChange?: (name: keyof KeyValueObj, value: string) => void;
  setFormData: React.Dispatch<React.SetStateAction<KeyValueObj>>;
}

// MED-723: `productDetails` used to be passed in and read directly by the
// fields below. It is gone: the order's answers are now merged into `formData`
// once by the parent (see buildPrefillFromSource in ./index.tsx), so every
// field here has a single source. Reading the product alongside the form state
// was what let a re-render put the order's value back over a user's edit.
export default function DeliveryInfo({
  formData,
  setFormData,
  onContinue,
  onBack,
}: IProps) {
  const [senderAddressType, setSenderAddressType] = useState("company_address");
  // MED-723: earliest selectable delivery date, fixed at mount so the picker's
  // floor cannot move under a form left open across midnight.
  const [minDeliveryDate] = useState(todayAsDateString);
  const [errorMessages] = useState<string[]>([]);
  const [stateValues, setStateValues] = useState<KeyValueObj>({});
  const { data: logisticsCompanies } = useQuery(
    LOGISTICS_COMPANIES_URL + `?state=${Object?.values(stateValues)[0]}`,
  );

  const handleUpdateLogistic = (name: string, value: (string | number)[]) => {
    if (!Array.isArray(value) || value.length === 0) {
      setFormData((prev) => ({
        ...prev,
        [name]: [],
      }));
      return;
    }

    const selectedCompanies = logisticsCompanies.filter(
      (company: KeyValueObj) => value.includes(company.id),
    );

    setFormData((prev) => ({
      ...prev,
      [name]: Array.isArray(selectedCompanies)
        ? selectedCompanies
        : [selectedCompanies],
    }));
  };

  function setDropOffAddress(_: (prev: any) => any): void {
    throw new Error("Function not implemented.");
  }

  // MED-723: `category` joined this list because the field became editable.
  // It is prepopulated from the product, so it was previously always set — but
  // a user can now clear it, and DeliveryItem in the logistics service refuses
  // a delivery without one. The temperature band is required on the same
  // grounds, and only for a cold-storage load, which is exactly when that
  // service demands it.
  const requiredFields = [
    "logistics_company",
    "vehicle_needed",
    "expected_delivery_date",
    "recipent_phone_number",
    "sender_phone_number",
    "pickup_option",
    "category",
    ...(formData?.category === "cold_storage"
      ? ["min_temperature", "max_temperature"]
      : []),
  ];

  // MED-723: the two ends of the band are separate inputs, so neither field's
  // own rules can see the other. The relationship is checked here instead —
  // the logistics service refuses an inverted band outright
  // (DeliveryItem#valid_temperature_range), so letting Continue through would
  // only move the failure to submit.
  const isTemperatureBandValid = (() => {
    if (formData?.category !== "cold_storage") return true;
    const min = parseFloat(formData?.min_temperature);
    const max = parseFloat(formData?.max_temperature);
    if (Number.isNaN(min) || Number.isNaN(max)) return true; // required-ness covers this
    return min <= max;
  })();

  const isFormValid =
    requiredFields.every((field) => {
      const value = formData[field];

      // For array or object fields
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object" && value !== null)
        return Object.keys(value).length > 0;

      // MED-723: zero counts as supplied. `Boolean(value)` treated a minimum
      // temperature of 0 °C as a missing field and would not let the form
      // through — which is a temperature a cold chain very plainly reaches.
      if (typeof value === "number") return !Number.isNaN(value);

      // For strings/numbers
      return Boolean(value && value !== "");
    }) && isTemperatureBandValid;

  const stateOptions = statesOnly.map((item) => ({
    id: item.id,
    title: item.name,
  }));

  return (
    <>
      <div className="w-full p-1.5 gap-10 ">
        <div className="my-10">
          <H3 className="my-3">Logistics Provider Details</H3>
          <MarketPrice />

          <div className="my-5">
            <div className="flex gap-5">
              {/*
                MED-723: both were `readOnly` with an `initialValue` taken raw
                from the product, and both are now prepopulated by the parent
                and editable — a delivery can genuinely differ from the order
                (goods repacked into an insulated box for a hot run), and the
                user has to be able to say so.

                `initialValue` is gone rather than kept: it fed the RAW
                `storage_requirement` ("cold storage") into a dropdown whose
                options are underscored ("cold_storage"), so it matched nothing,
                and it was dead in any case — Select only seeds it through
                `updateData`, which neither of these passed. The parent's
                prefill (see buildPrefillFromSource) is now the single writer.

                `updateData` + `returnId` are what make the edit stick: without
                `updateData` picking an option changed only what the closed box
                displayed and never reached `formData`, so a "change" the user
                made was silently dropped on submit.
              */}
              <Select
                name="package_type"
                options={packageType}
                placeholder="Select the packaging this ships in"
                prompt="Packaging Type"
                fullWidth={true}
                shortWidth={false}
                indexLevel={10}
                formValues={formData}
                setFormValues={setFormData}
                updateData={(name, value) =>
                  setFormData((prev: KeyValueObj) => ({ ...prev, [name]: value }))
                }
                returnId
              />
              <Select
                name="category"
                options={temperatureOption}
                placeholder="Select a temperature category"
                prompt="Temperature Categories"
                fullWidth={true}
                shortWidth={false}
                indexLevel={9}
                formValues={formData}
                setFormValues={setFormData}
                updateData={(name, value) =>
                  setFormData((prev: KeyValueObj) => ({ ...prev, [name]: value }))
                }
                returnId
              />
            </div>

            {/*
              MED-723: keyed off the temperature category IN THE FORM, not off
              the product's stored `storage_requirement`. The category is now
              editable, and gating on the frozen product value meant a user who
              switched a non-cold product to cold storage was never shown the
              temperature boxes the logistics service then rejects the delivery
              for missing (DeliveryItem validates min/max presence when
              category is cold_storage) — and one who switched the other way
              kept submitting temperatures for an ambient load.

              Marked `required` for the same reason: it is a hard rule on the
              receiving end, so failing it here beats failing it on submit.
            */}
            {formData?.category === "cold_storage" && (
              <div className="flex gap-5 mt-5 ">
                <Input
                  placeholder={"2"}
                  inputType={"number"}
                  prompt={"Minimum Temperature('C)"}
                  fullWidth={false}
                  shortWidth={false}
                  name={"min_temperature"}
                  indexLevel={8}
                  formValues={formData}
                  initialValue={formData?.min_temperature}
                  setFormValues={setFormData}
                  required
                  min={COLD_STORAGE_FLOOR_C}
                  max={COLD_STORAGE_CEILING_C}
                  rules={{
                    minNumber: {
                      expectedValue: COLD_STORAGE_FLOOR_C,
                      errorMessage: `Must be at or above ${COLD_STORAGE_FLOOR_C}°C`,
                    },
                    maxNumber: {
                      expectedValue: COLD_STORAGE_CEILING_C,
                      errorMessage: `Cold storage must be at or below ${COLD_STORAGE_CEILING_C}°C`,
                    },
                  }}
                  updateData={(name: string, value: any) =>
                    setFormData((prev: any) => ({
                      ...prev,
                      [name]: value,
                    }))
                  }
                  returnId
                />

                <Input
                  placeholder="8"
                  prompt={"Maximum Temperature('C)"}
                  fullWidth={false}
                  shortWidth={false}
                  inputType={"number"}
                  name={"max_temperature"}
                  indexLevel={7}
                  formValues={formData}
                  initialValue={formData?.max_temperature}
                  setFormValues={setFormData}
                  required
                  min={COLD_STORAGE_FLOOR_C}
                  max={COLD_STORAGE_CEILING_C}
                  rules={{
                    minNumber: {
                      expectedValue: COLD_STORAGE_FLOOR_C,
                      errorMessage: `Must be at or above ${COLD_STORAGE_FLOOR_C}°C`,
                    },
                    maxNumber: {
                      expectedValue: COLD_STORAGE_CEILING_C,
                      errorMessage: `Cold storage must be at or below ${COLD_STORAGE_CEILING_C}°C`,
                    },
                  }}
                  updateData={(name: string, value: any) =>
                    setFormData((prev) => ({ ...prev, [name]: value }))
                  }
                />
              </div>
            )}

            {/*
              MED-723: `initialValue` is what displays a prepopulated number.
              Input keeps its own `currentValue` and never reads `formValues`
              for display — `formValues` is consulted only by the rules engine —
              so without this the dimensions would sit in `formData` and be
              submitted while the user looked at three empty boxes.

              Each was also passing the SCALAR (`formData?.length`) as
              `formValues`, where every other field passes the whole form
              object. Harmless only because these declare no rules; corrected so
              adding one later does not silently do nothing. `height` also read
              `formData.height` unguarded, which throws on the first render if
              the form state is ever absent.

              Editing is unaffected: Input reseeds from `initialValue` only when
              it changes, and after an edit it already holds the user's value.
            */}
            <div className="flex gap-5 mt-8">
              <Input
                placeholder={"22"}
                prompt={"Product Length (cm)"}
                fullWidth={false}
                shortWidth={false}
                name={"length"}
                inputType={"number"}
                indexLevel={6}
                formValues={formData}
                initialValue={formData?.length}
                setFormValues={setFormData}
                updateData={(name: string, value: any) =>
                  setFormData((prev: any) => ({
                    ...prev,
                    [name]: value,
                  }))
                }
                returnId
              />

              <Input
                placeholder="30"
                prompt="Product height (cm)"
                fullWidth={false}
                shortWidth={false}
                name={"height"}
                inputType={"number"}
                indexLevel={5}
                formValues={formData}
                initialValue={formData?.height}
                setFormValues={setFormData}
                updateData={(name: string, value: any) =>
                  setFormData((prev) => ({ ...prev, [name]: value }))
                }
              />
              <Input
                placeholder="30"
                prompt="Product width (cm)"
                fullWidth={false}
                shortWidth={false}
                name={"width"}
                inputType={"number"}
                indexLevel={5}
                formValues={formData}
                initialValue={formData?.width}
                setFormValues={setFormData}
                updateData={(name: string, value: any) =>
                  setFormData((prev) => ({ ...prev, [name]: value }))
                }
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-2 justify-center">
          <div className="w-[20rem]">
            <Select
              options={stateOptions}
              placeholder={"select a specific state"}
              prompt={"Filter By States "}
              name={""}
              fullWidth={true}
              shortWidth={false}
              indexLevel={3}
              formValues={stateValues}
              setFormValues={setStateValues}
              updateData={(name, value) => {
                setStateValues((prev) => ({
                  ...prev,
                  [name]: value,
                }));
                // MED-721: the logistics list is scoped to the selected
                // state, so companies chosen under the old state are no
                // longer valid once the state changes — clear rather than
                // leave a stale selection standing.
                setFormData((prev) => ({ ...prev, logistics_company: [] }));
              }}
            />
          </div>

          <MultiSelect
            options={getReceivingCompanyOptions(logisticsCompanies)}
            placeholder="Select Logistics Companies"
            updateData={handleUpdateLogistic}
            // MED-721: this was a no-op that suppressed MultiSelect's own
            // working local filter for nothing — the list is already loaded
            // upfront for the selected state, so it doesn't need a
            // server round-trip per keystroke.
            prompt="Logistics Company"
            fullWidth
            shortWidth
            name="logistics_company"
            indexLevel={2}
            formValues={formData}
            setFormValues={setFormData}
            returnId
            required
          />
        </div>

        <div className="mt-10 w-full">
          <H3 className="my-5">Other Details</H3>
          <div className="flex gap-5">
            <Select
              name="vehicle_needed"
              options={vehicleOptions}
              placeholder="Bus"
              prompt="Preferred Vehicle Type"
              fullWidth={false}
              shortWidth={false}
              indexLevel={1}
              formValues={formData}
              setFormValues={setFormData}
              updateData={(name, value) =>
                setFormData((prev: any) => ({ ...prev, [name]: value }))
              }
              returnId
            />

            <Input
              placeholder="DD-MM-YYYY"
              prompt="Expected Delivery Date"
              fullWidth={false}
              shortWidth={false}
              name="expected_delivery_date"
              inputType="date"
              // MED-723: displays the date prepopulated from the order (see
              // buildPrefillFromSource). Same reason as the dimensions above —
              // Input renders `currentValue`, not `formValues`.
              initialValue={formData?.expected_delivery_date}
              // MED-723: the floor is today, as a "YYYY-MM-DD" calendar day.
              // It used to be `Date.now() + 24h` as a full ISO timestamp, which
              // the picker's date-only value (parsed as UTC midnight) could
              // never beat — so tomorrow was rejected as "in the past" at every
              // hour of the day while two days ahead passed. `min` greys out
              // the earlier days in the picker; the rule still checks a typed
              // value, since `min` alone is not enforcement.
              min={minDeliveryDate}
              rules={{
                notBeforeDate: {
                  expectedValue: minDeliveryDate,
                  errorMessage: "Date cannot be in the past",
                },
              }}
              indexLevel={100}
              formValues={formData}
              setFormValues={setFormData}
              required
              updateData={(name: string, value: any) =>
                setFormData((prev: any) => ({ ...prev, [name]: value }))
              }
              returnId
            />
          </div>

          <div className="flex gap-5 mt-8">
            <Input
              placeholder={"07012345678"}
              prompt={"Recipent's Secondary Phone Number"}
              fullWidth={false}
              shortWidth={false}
              name={"recipent_phone_number"}
              indexLevel={98}
              formValues={formData}
              setFormValues={formData?.recipent_phone_number}
              updateData={(name: string, value: any) =>
                setFormData((prev: any) => ({
                  ...prev,
                  [name]: value,
                }))
              }
              returnId
            />

            <Input
              placeholder="09012345678"
              prompt="Sender's Secondary Phone Number"
              fullWidth={false}
              shortWidth={false}
              name="sender_phone_number"
              indexLevel={97}
              setFormValues={setFormData}
              formValues={formData}
              updateData={(name: string, value: any) =>
                setFormData((prev) => ({ ...prev, [name]: value }))
              }
            />
          </div>
        </div>

        <div className="my-5"></div>

        <H3>Pick-up Type</H3>

        <div className="grid grid-cols-2 gap-6 mt-4">
          <span className="radio-wrapper ">
            <input
              type="radio"
              value="logistics_pickup"
              name="pickup_option"
              id="logistics-pickup"
              checked={formData?.pickup_option == "logistics_pickup"}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  pickup_option: e.target.value,
                }))
              }
            />
            <label htmlFor="logistics-pickup" className="mx-2">
              Logistic Pickup - The logistic company will come to either your
              office address or an address you will specify.
            </label>
          </span>
          <span className="radio-wrapper">
            <input
              type="radio"
              value="sender_drop_off"
              name="pickup_option"
              checked={formData?.pickup_option === "sender_drop_off"}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  pickup_option: e.target.value,
                }))
              }
            />
            <label htmlFor="sender_drop_off" className="mx-2">
              Sender Dropoff - You will take the items to the logistics
              company's address
            </label>
          </span>
        </div>

        {formData?.pickup_option === "logistics_pickup" ? (
          <div className="my-5">
            <H3>Where should the logistic company pick up the items</H3>
            <div className="flex justify-between">
              <span className="">
                <input
                  type="radio"
                  value="company_address"
                  name="sender_address_type"
                  id="company_address"
                  checked={senderAddressType === "company_address"}
                  onChange={(e) => setSenderAddressType(e.target.value)}
                />

                <label htmlFor="company_address" className="mx-2">
                  Company Address
                </label>
              </span>
              <span className="radio-wrapper">
                <input
                  type="radio"
                  value="custom_address"
                  name="sender_address_type"
                  id="custom_address"
                  checked={senderAddressType === "custom_address"}
                  onChange={(e) => setSenderAddressType(e.target.value)}
                />

                <label htmlFor="custom_address" className="mx-2">
                  Custom Address
                </label>
              </span>
            </div>
          </div>
        ) : (
          <div className="my-5">
            <H3>Logistic Provider Address</H3>
            <div className="">
              {getLogisticCompaniesDetails(formData?.logistics_company)?.map(
                (company: KeyValueObj, i: number) => (
                  <div key={company.id} className="p-4 border rounded mb-3">
                    <h1 key={i}>{company[0]?.value}</h1>
                    <h1 key={i}>{company[1]?.value}</h1>
                  </div>
                ),
              )}
            </div>
          </div>
        )}

        {formData?.pickup_option === "logistics_pickup" &&
        senderAddressType === "custom_address" ? (
          <Form
            formInputs={getNewDeliveryAddressFormInputs()}
            processInputs={() => {}}
            removeSubmitButton
            removePadding
            externalUpdateData={(name, value) =>
              setDropOffAddress((prev: any) => ({ ...prev, [name]: value }))
            }
          />
        ) : null}

        {formData?.pickup_option === "logistics_pickup" &&
        senderAddressType === "company_address" ? (
          <AddressManager
            selectedAddress={formData?.pickup_address}
            setSelectedAddress={(address) =>
              setFormData((prev) => ({ ...prev, pickup_address: address }))
            }
            // MED-721: same as new-delivery's address step — deleting a
            // saved address mid-flow is out of place.
            hideDelete
          />
        ) : null}
        {errorMessages?.length > 0 ? (
          <div>
            {errorMessages.map((error: string, i: number) => (
              <p className="error" key={i}>
                {error}
              </p>
            ))}
          </div>
        ) : null}
        {/*
          MED-723: an inverted band disables Continue, and a disabled button
          with nothing next to it leaves the user hunting for which of two
          separate boxes is wrong. The per-field rules cannot say this — neither
          input can see the other's value.
        */}
        {!isTemperatureBandValid ? (
          <p className="error">
            Minimum temperature must be at or below the maximum temperature.
          </p>
        ) : null}
        <div className="flex-row  gap-5 mt-10  p-5 pb-20">
          <Button secondary onClick={onBack}>
            Back
          </Button>
          <Button onClick={onContinue} disabled={!isFormValid}>
            {" "}
            Continue
          </Button>
        </div>
      </div>
    </>
  );
}
